/**
 * Import & migration.
 *
 * Upload a sheet, check how its columns were read, see every row's status,
 * then commit. Only valid rows are written. Rows missing a batch number, an
 * expiry or a cost are held for somebody to resolve — nothing is filled in —
 * and importing the same sheet again writes nothing twice, because each
 * record is matched on what it describes rather than on the file.
 *
 * Two kinds are a pharmacy's own two-step. A classification sheet is prepared
 * by whoever keeps the catalogue and written only when a pharmacist approves
 * it; opening stock is counted by whoever keeps stock and written only when a
 * stock approver does. Each approval states what was checked, and the server
 * decides who may do which.
 */

import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Upload } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { useWorkingLocation } from '../../lib/locationContext';
import { money } from '../../lib/format';
import { Badge, Button, Card, CardHeader, ConfirmDialog, EmptyState, ErrorState, Select, Spinner, Textarea, cn } from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { usePharmacy, usePharmacyUpload, usePharmacyWrite, type ListOf } from './data';
import { ErrorLine, StatusBadge, Table, Td, Tile, errorText } from './parts';

type Kind = 'products' | 'opening_stock' | 'counted_stock' | 'suppliers' | 'customers' | 'opening_balances' | 'classification';

const KINDS: Array<{ value: Kind; label: string; about: string; columns: string; needsLocation: boolean }> = [
  {
    value: 'products',
    label: 'Products & units',
    about: 'Medicines and retail items, with their selling units and prices. Import these first.',
    columns: 'Name, SKU, Generic, Base unit, Price, Pack unit, Pack size, Pack price, Carton unit, Carton size, Carton price, Class, Barcode',
    needsLocation: false,
  },
  {
    value: 'classification',
    label: 'Classification sheet',
    about:
      'Products already in CenoX: medicine or not, class and storage, filled in from the worksheet. Written only when a pharmacist approves it.',
    columns:
      'CenoX ID, Medicine? (yes/no), Class (OTC, P, POM, Restricted), Storage (room, cool, refrigerated, frozen), Base unit — download it from Medicines & products',
    needsLocation: false,
  },
  {
    value: 'opening_stock',
    label: 'Opening stock & batches',
    about:
      'What is on the shelf today, by batch. Quantity is in BASE units (tablets, not packs); cost is per base unit. Approved by a stock approver; expired batches are counted and quarantined.',
    columns: 'Product (SKU, name or barcode), Batch, Expiry, Qty (base units), Unit cost (per base unit), Supplier, Manufacturing date, Storage confirmed (yes for cool, fridge or freezer items)',
    needsLocation: true,
  },
  {
    value: 'counted_stock',
    label: 'Counted, awaiting batch details',
    about:
      'Batch-tracked medicines counted on the shelf before their batch numbers and expiry dates were written down. Kept as a count at its cost — never stock, never sold — until a pharmacist records the batches as opening stock.',
    columns: 'Product (SKU, name or barcode), Qty (base units), Unit cost (per base unit)',
    needsLocation: true,
  },
  { value: 'suppliers', label: 'Suppliers', about: 'Who you buy from.', columns: 'Name, Code, Phone, Email, Address', needsLocation: false },
  { value: 'customers', label: 'Customers', about: 'Customer contacts. Patients with clinical details are added under Patients.', columns: 'Name, Phone, Email, Address', needsLocation: false },
  {
    value: 'opening_balances',
    label: 'Opening balances',
    about: 'What customers owed you, and what you owed suppliers, on the day you moved to CenoX. Not revenue and not invoices.',
    columns: 'Party, Kind (customer/supplier), Amount, As of',
    needsLocation: true,
  },
];

interface Preview {
  /** Every column the sheet had, from the server. */
  headers?: string[];
  id: string;
  kind: Kind;
  filename: string;
  status: string;
  branchId: string | null;
  fields: string[];
  requiredFields: string[];
  mapping: Record<string, string>;
  counts: Record<string, number>;
  sameFileCommittedBefore: { runId: string; at: string } | null;
  rows: Array<{ row_number: number; data: Record<string, unknown>; status: string; errors: string[] }>;
  written?: number;
  preparedBy?: string | null;
  approvedBy?: string | null;
  approvalReason?: string | null;
  needsApprovalReason?: boolean;
  mayApprove?: boolean;
  /** The mapping the approver saw; sent back so a later change is refused. */
  mappingVersion?: string;
  /** file: an uploaded sheet. in_app: stock typed into CenoX, which has no columns to map. */
  source?: 'file' | 'in_app';
}

interface Reconciliation {
  kind: Kind;
  rows: number;
  counts: Record<string, number>;
  quantity?: { source: number; imported: number; heldForResolution: number };
  value?: { source: string; imported: string };
  difference?: number;
  balance?: { source: string; imported: string };
}

interface RunRow {
  id: string;
  kind: Kind;
  filename: string;
  status: string;
  summary: Record<string, number>;
  created_at: string;
  prepared_by?: string | null;
  awaitingYourApproval?: boolean;
}

/** A line typed into CenoX, said the way it was entered rather than as raw fields. */
function typedLine(d: Record<string, unknown>): string {
  return [
    String(d.name ?? 'Product'),
    `${String(d.quantity ?? '')} base units`,
    d.batchNumber ? `batch ${String(d.batchNumber)}` : 'no batch',
    d.expiryDate ? `expires ${String(d.expiryDate)}` : 'no expiry',
    d.manufacturedOn ? `made ${String(d.manufacturedOn)}` : null,
    d.unitCost !== null && d.unitCost !== undefined && d.unitCost !== '' ? `cost ${String(d.unitCost)}` : 'no cost',
    d.storageConfirmed === true ? 'storage confirmed' : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

const STATUS_ORDER = ['valid', 'imported', 'incomplete', 'invalid', 'duplicate', 'unchanged'];

export function PharmacyImportPage(): ReactNode {
  const { can, business } = useAuth();
  // ?run= opens one run directly: stock entered in CenoX links here for its approval.
  const [params, setParams] = useSearchParams();
  const runId = params.get('run');
  const setRunId = (id: string | null) => setParams(id ? { run: id } : {});
  // Who may prepare or approve which kind is the server's call; this only decides whether the page is worth showing.
  if (!can('imports:manage') && !can('pharmacy:catalogue') && !can('pharmacy:stock') && !can('pharmacy:stock_approve')) {
    return (
      <div className="space-y-5">
        <PageHeader title="Import & migration" />
        <EmptyState title="You cannot import" description="Importing needs the imports permission, or a pharmacy catalogue or stock role. Ask the owner." />
      </div>
    );
  }
  const c = business?.currencyCode ?? 'NGN';
  return (
    <div className="space-y-5">
      <PageHeader
        title="Import & migration"
        description="Bring products, stock, suppliers, customers and balances in from Excel or CSV — previewed, validated and safe to repeat"
      />
      {runId ? <RunView runId={runId} currency={c} onDone={() => setRunId(null)} /> : <UploadForm onStaged={setRunId} />}
      <PreviousRuns onOpen={setRunId} />
    </div>
  );
}

function UploadForm({ onStaged }: { onStaged: (id: string) => void }): ReactNode {
  const { locations } = useWorkingLocation();
  const runs = usePharmacy<ListOf<RunRow> & { kinds?: Kind[] }>('/imports');
  const allowed = runs.data?.kinds;
  const offered = allowed ? KINDS.filter((k) => allowed.includes(k.value)) : KINDS;
  const [chosen, setKind] = useState<Kind | null>(null);
  const kind: Kind = chosen && offered.some((k) => k.value === chosen) ? chosen : (offered[0]?.value ?? 'products');
  const [branchId, setBranchId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const upload = usePharmacyUpload<Preview>('/imports');
  const spec = KINDS.find((k) => k.value === kind)!;
  if (allowed && offered.length === 0) {
    return <EmptyState title="Nothing for you to prepare" description="Sheets waiting for your approval are listed below." />;
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!file) return;
    const form = new FormData();
    form.append('kind', kind);
    if (spec.needsLocation) form.append('branchId', branchId);
    form.append('file', file);
    upload.mutate({ form }, { onSuccess: (preview) => onStaged(preview.id) });
  }

  return (
    <Card>
      <CardHeader title="1. What are you importing?" />
      <form onSubmit={submit} className="space-y-4">
        <div role="radiogroup" aria-label="What the sheet contains" className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {offered.map((k) => (
            <button
              key={k.value}
              type="button"
              role="radio"
              aria-checked={kind === k.value}
              onClick={() => setKind(k.value)}
              className={cn(
                'rounded-lg border p-3 text-left transition-colors',
                kind === k.value ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-500' : 'border-slate-200 hover:bg-slate-50',
              )}
            >
              <p className="font-medium text-slate-900">{k.label}</p>
              <p className="mt-0.5 text-xs text-slate-500">{k.about}</p>
            </button>
          ))}
        </div>
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <span className="font-medium">Columns:</span> {spec.columns}. Headers are matched automatically; you can correct the
          mapping before committing.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-end">
          {spec.needsLocation && (
            <Select label="Location" required value={branchId} onChange={(e) => setBranchId(e.target.value)} placeholder="Choose the location this sheet is for" options={locations.map((l) => ({ value: l.id, label: l.name }))} />
          )}
          <label className="block">
            <span className="label-base">Spreadsheet (.xlsx or .csv)</span>
            <input
              type="file"
              accept=".xlsx,.csv"
              required
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-brand-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-brand-700"
            />
          </label>
        </div>
        <ErrorLine error={upload.error} />
        <Button type="submit" icon={<Upload className="size-4" />} loading={upload.isPending} disabled={!file || (spec.needsLocation && !branchId)}>
          Upload and preview
        </Button>
      </form>
    </Card>
  );
}

export function RunView({
  runId,
  currency,
  onDone,
  doneLabel = 'Start another',
}: {
  runId: string;
  currency: string;
  onDone: () => void;
  doneLabel?: string;
}): ReactNode {
  const preview = usePharmacy<Preview>(`/imports/${runId}`);
  const setMapping = usePharmacyWrite<Record<string, string>, Preview>('put', `/imports/${runId}/mapping`);
  const commit = usePharmacyWrite<{ reason?: string; mappingVersion?: string }, Preview>('post', `/imports/${runId}/commit`);
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [filter, setFilter] = useState('');
  const p = preview.data;
  const headers = useMemo(() => p?.headers ?? (p?.rows[0] ? Object.keys(p.rows[0].data) : []), [p]);

  if (preview.isError) return <ErrorState message="Could not load the import." onRetry={() => void preview.refetch()} />;
  if (!p) return <Spinner label="Reading the sheet" />;

  const committed = p.status === 'committed';
  const rows = filter ? p.rows.filter((r) => r.status === filter) : p.rows;
  const statuses = STATUS_ORDER.filter((s) => p.counts[s]);

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader
          title={`2. ${p.filename}`}
          subtitle={`${KINDS.find((k) => k.value === p.kind)?.label ?? p.kind} · ${p.rows.length} rows`}
          action={<Button variant="ghost" onClick={onDone}>{doneLabel}</Button>}
        />
        {p.sameFileCommittedBefore && (
          <p className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
            This exact file was committed before ({new Date(p.sameFileCommittedBefore.at).toLocaleString()}). Committing again is
            safe — records already in CenoX are matched and skipped — but it will add nothing new.
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          {statuses.map((s) => (
            <button key={s} type="button" onClick={() => setFilter(filter === s ? '' : s)} className={cn('rounded-full', filter === s && 'ring-2 ring-brand-500')}>
              <StatusBadge status={s} />
              <span className="ml-1 text-sm tabular-nums text-slate-700">{p.counts[s]}</span>
            </button>
          ))}
        </div>
      </Card>

      {!committed && p.source !== 'in_app' && (
        <Card>
          <CardHeader title="3. Check the columns" subtitle="Which column of the sheet holds each field. Required fields are starred." />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {p.fields.map((field) => (
              <Select
                key={field}
                label={`${field}${p.requiredFields.includes(field) ? ' *' : ''}`}
                value={p.mapping[field] ?? ''}
                placeholder="Not in this sheet"
                options={headers.map((h) => ({ value: h, label: h }))}
                onChange={(e) => setMapping.mutate({ ...p.mapping, [field]: e.target.value })}
              />
            ))}
          </div>
          <div className="mt-3"><ErrorLine error={setMapping.error} /></div>
        </Card>
      )}

      <Card padded={false}>
        <div className="p-4 sm:p-5">
          <CardHeader title="Rows" subtitle={filter ? `Showing ${filter.replace(/_/g, ' ')} rows` : 'Every row and what will happen to it'} />
        </div>
        <Table head={['Row', 'Status', 'Data', 'Why']} minWidth={720}>
          {rows.slice(0, 500).map((r) => (
            <tr key={r.row_number}>
              <Td className="tabular-nums">{r.row_number}</Td>
              <Td><StatusBadge status={r.status} /></Td>
              <Td className="max-w-md truncate text-xs text-slate-600">
                {p.source === 'in_app'
                  ? typedLine(r.data)
                  : Object.entries(r.data).map(([k, v]) => `${k}: ${String(v ?? '')}`).join(' · ')}
              </Td>
              <Td className="text-xs text-slate-600">{r.errors.join('; ') || '—'}</Td>
            </tr>
          ))}
        </Table>
        {rows.length > 500 && <p className="px-5 py-3 text-xs text-slate-500">First 500 rows shown.</p>}
      </Card>

      {!committed && p.mayApprove === false ? (
        <Card>
          <CardHeader title="4. Waiting for approval" />
          <p className="text-sm text-slate-600">
            {p.kind === 'classification'
              ? 'A pharmacist approves this sheet before anything is classified. They will find it under Previous imports.'
              : 'Somebody who approves stock writes this sheet after checking it. They will find it under Previous imports.'}
          </p>
        </Card>
      ) : !committed ? (
        <Card>
          <CardHeader title={p.needsApprovalReason ? '4. Approve' : '4. Commit'} />
          {p.preparedBy && <p className="mb-2 text-sm text-slate-600">Prepared by {p.preparedBy}.</p>}
          <p className="text-sm text-slate-600">
            {p.counts.valid ?? 0} valid row(s) will be written. Incomplete rows are held — CenoX does not invent a
            {p.kind === 'classification' ? ' class or a storage condition' : ' batch number, an expiry date or a cost'} — and
            invalid or duplicate rows are skipped.
          </p>
          {p.needsApprovalReason && (
            <div className="mt-3 max-w-xl">
              <Textarea
                label="What did you check?"
                required
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={p.kind === 'classification' ? 'e.g. Checked each item against the pack and the NAFDAC register' : 'e.g. Counted shelf by shelf with the stockkeeper on 1 Nov'}
              />
            </div>
          )}
          <div className="mt-3"><ErrorLine error={commit.error} /></div>
          <Button
            className="mt-3"
            onClick={() => setConfirming(true)}
            disabled={!p.counts.valid || (p.needsApprovalReason === true && reason.trim().length < 3)}
          >
            {p.needsApprovalReason ? 'Approve and write' : 'Commit import'}
          </Button>
          <ConfirmDialog
            open={confirming}
            onClose={() => setConfirming(false)}
            onConfirm={() =>
              commit.mutate(p.needsApprovalReason ? { reason: reason.trim(), mappingVersion: p.mappingVersion } : {}, { onSettled: () => setConfirming(false) })
            }
            title={p.needsApprovalReason ? 'Approve this sheet?' : 'Commit this import?'}
            tone="primary"
            confirmLabel={`Write ${p.counts.valid ?? 0} row(s)`}
            loading={commit.isPending}
            error={errorText(commit.error)}
            description={
              <ul className="list-disc space-y-1 pl-4">
                <li>Only valid rows are written.</li>
                <li>Incomplete rows stay held for resolution; nothing is filled in for you.</li>
                <li>Opening stock and balances are recorded as what they are — no sales, receipts or invoices are invented.</li>
                {p.kind === 'opening_stock' && <li>Expired batches are counted and quarantined on arrival; they never sell.</li>}
                {p.kind === 'counted_stock' && (
                  <li>These are kept as a count, not stock: nothing goes on hand or on the till until a pharmacist records each item’s batches as opening stock.</li>
                )}
                {p.kind === 'classification' && <li>Each item keeps its record, code and price. Your name and reason go on every one.</li>}
                <li>Importing the same records again is safe: they are matched and nothing is added twice.</li>
              </ul>
            }
          />
        </Card>
      ) : (
        <>
          {(p.approvedBy || p.approvalReason) && (
            <p className="text-sm text-slate-600">
              {p.preparedBy ? `Prepared by ${p.preparedBy}. ` : ''}Approved by {p.approvedBy ?? '—'}
              {p.approvalReason ? `: “${p.approvalReason}”` : ''}
            </p>
          )}
          <ReconciliationCard runId={runId} currency={currency} written={commit.data?.written} />
        </>
      )}
    </div>
  );
}

function ReconciliationCard({ runId, currency, written }: { runId: string; currency: string; written?: number | undefined }): ReactNode {
  const recon = usePharmacy<Reconciliation>(`/imports/${runId}/reconciliation`);
  const r = recon.data;
  return (
    <Card>
      <CardHeader
        title="5. Reconciliation"
        subtitle="The sheet against what CenoX now holds, read back from the records the import produced"
        action={written !== undefined ? <Badge tone="success">{written} written</Badge> : undefined}
      />
      {recon.isError ? (
        <ErrorState message="Could not reconcile." onRetry={() => void recon.refetch()} />
      ) : !r ? (
        <Spinner />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Tile label="Rows in sheet" value={r.rows} />
            {r.quantity && (
              <>
                <Tile label="Quantity in sheet" value={r.quantity.source} sub="base units" />
                <Tile label="Quantity imported" value={r.quantity.imported} sub="base units" tone="good" />
                <Tile label="Held for resolution" value={r.quantity.heldForResolution} sub="base units" tone={r.quantity.heldForResolution ? 'warn' : 'plain'} />
              </>
            )}
            {r.value && (
              <>
                <Tile label="Value in sheet" value={money(r.value.source, currency)} sub="at cost" />
                <Tile label="Value imported" value={money(r.value.imported, currency)} sub="at batch cost" />
              </>
            )}
            {r.balance && (
              <>
                <Tile label="Balances in sheet" value={money(r.balance.source, currency)} />
                <Tile label="Balances imported" value={money(r.balance.imported, currency)} tone="good" />
              </>
            )}
          </div>
          {r.difference !== undefined && (
            <p className={cn('flex items-center gap-2 text-sm', r.difference === 0 ? 'text-emerald-700' : 'text-red-700')}>
              {r.difference === 0 ? <CheckCircle2 className="size-4" aria-hidden /> : <AlertTriangle className="size-4" aria-hidden />}
              {r.difference === 0
                ? 'Every unit in the sheet is either imported or held for resolution.'
                : `${r.difference} base units are unaccounted for — check invalid and duplicate rows.`}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {Object.entries(r.counts).map(([status, n]) => (
              <span key={status} className="inline-flex items-center gap-1"><StatusBadge status={status} /><span className="text-sm tabular-nums">{n}</span></span>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function PreviousRuns({ onOpen }: { onOpen: (id: string) => void }): ReactNode {
  const runs = usePharmacy<ListOf<RunRow>>('/imports');
  if (!runs.data || runs.data.data.length === 0) return null;
  return (
    <Card padded={false}>
      <div className="p-4 sm:p-5"><CardHeader title="Previous imports" /></div>
      <Table head={['When', 'What', 'File', 'Prepared by', 'Status', 'Summary', '']} minWidth={720}>
        {runs.data.data.map((run) => (
          <tr key={run.id}>
            <Td className="whitespace-nowrap">{new Date(run.created_at).toLocaleString()}</Td>
            <Td>{KINDS.find((k) => k.value === run.kind)?.label ?? run.kind}</Td>
            <Td className="max-w-[12rem] truncate">{run.filename}</Td>
            <Td>{run.prepared_by ?? '—'}</Td>
            <Td>{run.awaitingYourApproval ? <Badge tone="warning">Awaiting your approval</Badge> : <StatusBadge status={run.status} />}</Td>
            <Td className="text-xs text-slate-600">{Object.entries(run.summary ?? {}).map(([k, v]) => `${k} ${v}`).join(' · ') || '—'}</Td>
            <Td><Button size="sm" variant="ghost" onClick={() => onOpen(run.id)}>Open</Button></Td>
          </tr>
        ))}
      </Table>
    </Card>
  );
}
